/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useEffect, useContext, useState } from 'react';
import { Button, Form, FormGroup, Label, Input, Col, InputGroup, InputGroupAddon, Badge } from 'reactstrap';
import Alert from 'react-s-alert';
import { Prompt } from 'react-router-dom';

// context imports
import { ThemeContext } from '../../contexts/ThemeContext'

// import services
import AdminService from '../../services/AdminService';
import GeneralService from '../../services/GeneralService';

// local imports
import ErrorBoundary from '../general/ErrorBoundary';

const Dashboard = props => {

    // states
    const [dashboardData, setDashboardData] = useState({
        timeSlotSize: 30,
        advanceBookingLimit: 0,
        minimumAgeForClient: 0,
        GSTPercentage: 18,
        minimumReportCharge: 0,
        minimumServiceCharges: { call: 0, chat: 0, video: 0, inPerson: 0 },
        userTitles: [],
        providerSpecialities: [],
        providerLanguages: [],
        clientSubscriptionPlanRange: { from: 0, to: 0, interval: 0 }
    });
    const [timeSlotSizeUpdated, setTimeSlotSizeUpdated] = useState(false);
    const [advanceBookingLimitUpdated, setAdvanceBookingLimitUpdated] = useState(false);
    const [minimumAgeForClientUpdated, setMinimumAgeForClientUpdated] = useState(false);
    const [GSTPercentageUpdated, setGSTPercentageUpdated] = useState(false);
    const [minimumReportChargeUpdated, setMinimumReportChargeUpdated] = useState(false);
    const [minimumServiceChargesUpdated, setMinimumServiceChargesUpdated] = useState(false);
    const [userTitlesUpdated, setUserTitlesUpdated] = useState(false);
    const [providerSpecialitiesUpdated, setProviderSpecialitiesUpdated] = useState(false);
    const [providerLanguagesUpdated, setProviderLanguagesUpdated] = useState(false);
    const [clientSubscriptionPlanRangeUpdated, setClientSubscriptionPlanRangeUpdated] = useState(false)
    const [isLoading1, setIsLoading1] = useState(false);
    const [isLoading2, setIsLoading2] = useState(false);
    const [isLoading3, setIsLoading3] = useState(false);
    const [isLoading4, setIsLoading4] = useState(false);
    const [isLoading5, setIsLoading5] = useState(false);
    const [isLoading6, setIsLoading6] = useState(false);
    const [isLoading7, setIsLoading7] = useState(false);
    const [isLoading8, setIsLoading8] = useState(false);
    const [isLoading9, setIsLoading9] = useState(false);
    const [isLoading10, setIsLoading10] = useState(false);

    // contexts
    const { theme } = useContext(ThemeContext);

    // use Effects
    useEffect(() => {
        AdminService.getDashboardData().then(data => {
            try {
                setDashboardData(data.siteVariable)
            } catch (err) {
                Alert.error("Some error occured, please try again later or report support team.", {
                    position: 'top-right',
                    effect: 'slide'
                });
                GeneralService.logError('at Admin Dashboard useEffect', 'Error in try catch block', err.stack);
            }
        });
        return () => {
            window.onbeforeunload = undefined;
            setDashboardData();
            setTimeSlotSizeUpdated(false);
            setAdvanceBookingLimitUpdated(false);
            setMinimumAgeForClientUpdated(false);
            setGSTPercentageUpdated(false);
            setMinimumReportChargeUpdated(false);
            setMinimumServiceChargesUpdated(false);
            setUserTitlesUpdated(false);
            setProviderSpecialitiesUpdated(false);
            setProviderLanguagesUpdated(false);
            setClientSubscriptionPlanRangeUpdated(false);
        }
    }, []);

    useEffect(() => {
        if (timeSlotSizeUpdated || advanceBookingLimitUpdated || minimumAgeForClientUpdated || GSTPercentageUpdated || minimumReportChargeUpdated || minimumServiceChargesUpdated || userTitlesUpdated || providerSpecialitiesUpdated || providerLanguagesUpdated || clientSubscriptionPlanRangeUpdated) {
            window.onbeforeunload = () => true
        } else {
            window.onbeforeunload = undefined;
        }
    }, [timeSlotSizeUpdated, advanceBookingLimitUpdated, minimumAgeForClientUpdated, GSTPercentageUpdated, minimumReportChargeUpdated, minimumServiceChargesUpdated, userTitlesUpdated, providerSpecialitiesUpdated, providerLanguagesUpdated, clientSubscriptionPlanRangeUpdated]);

    // handle functions
    const handleChange = e => {
        try {
            setDashboardData({ ...dashboardData, [e.target.name]: e.target.value });
            if (e.target.name == "timeSlotSize") {
                setTimeSlotSizeUpdated(true);
            } else if (e.target.name == "advanceBookingLimit") {
                setAdvanceBookingLimitUpdated(true);
            } else if (e.target.name == "minimumAgeForClient") {
                setMinimumAgeForClientUpdated(true);
            } else if (e.target.name == "GSTPercentage") {
                setGSTPercentageUpdated(true);
            } else if (e.target.name == "minimumReportCharge") {
                setMinimumReportChargeUpdated(true);
            } else if (e.target.name == "minimumServiceCharges") {
                setMinimumServiceChargesUpdated(true);
            } else if (e.target.name == "userTitles") {
                setUserTitlesUpdated(true);
            } else if (e.target.name == "providerSpecialities") {
                setProviderSpecialitiesUpdated(true);
            } else if (e.target.name == "providerLanguages") {
                setProviderLanguagesUpdated(true);
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Dashboard handleChange', 'Error in try catch block', err.stack);
        }
    }

    const handleAddItem = (type) => {
        try {
            let element = document.getElementById(type);
            const value = element.value;
            if (value) {
                if (type === 'title') {
                    setDashboardData({ ...dashboardData, userTitles: [...dashboardData.userTitles, value] });
                    setUserTitlesUpdated(true);
                }
                else if (type === 'language') {
                    setDashboardData({ ...dashboardData, providerLanguages: [...dashboardData.providerLanguages, value] });
                    setProviderLanguagesUpdated(true);
                }
                else if (type === 'speciality') {
                    setDashboardData({ ...dashboardData, providerSpecialities: [...dashboardData.providerSpecialities, value] });
                    setProviderSpecialitiesUpdated(true);
                }
                element.value = "";
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Dashboard handleAddItem', 'Error in try catch block', err.stack);
        }
    }

    const handleRemoveItem = (type, index) => {
        try {
            if (type === 'title') {
                let tempArray = [...dashboardData.userTitles];
                tempArray.splice(index, 1);
                setDashboardData({ ...dashboardData, userTitles: tempArray });
                setUserTitlesUpdated(true);
            }
            else if (type === 'language') {
                let tempArray = [...dashboardData.providerLanguages];
                tempArray.splice(index, 1);
                setDashboardData({ ...dashboardData, providerLanguages: tempArray });
                setProviderLanguagesUpdated(true);
            }
            else if (type === 'speciality') {
                let tempArray = [...dashboardData.providerSpecialities];
                tempArray.splice(index, 1);
                setDashboardData({ ...dashboardData, providerSpecialities: tempArray });
                setProviderSpecialitiesUpdated(true);
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Dashboard handleRemoveItem', 'Error in try catch block', err.stack);
        }
    }

    const handleMinimumServiceChargesChange = e => {
        try {
            setDashboardData({ ...dashboardData, minimumServiceCharges: { ...dashboardData.minimumServiceCharges, [e.target.name]: e.target.value } });
            setMinimumServiceChargesUpdated(true);
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Dashboard handleMiniumServiceChargeChange', 'Error in try catch block', err.stack);
        }
    }

    const handleClientSubscriptionPlanRangeChange = e => {
        try {
            setDashboardData({ ...dashboardData, clientSubscriptionPlanRange: { ...dashboardData.clientSubscriptionPlanRange, [e.target.name]: e.target.value } });
            setClientSubscriptionPlanRangeUpdated(true);
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Dashboard handleClientSubscriptionPlanRangeChange', 'Error in try catch block', err.stack);
        }
    }

    const handleUserTitlesSubmit = e => {
        e.preventDefault();
        try {
            setIsLoading7(true);
            AdminService.setUserTitles(dashboardData.userTitles)
                .then(data => {
                    Alert.info(data.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setUserTitlesUpdated(false);
                    setIsLoading7(false);
                })
                .catch(error => {
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading7(false);
                });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading7(false);
            GeneralService.logError('at Admin Dashboard handleUserTitlesSubmit', 'Error in try catch block', err.stack);
        }
    }

    const handleProviderSpecialitiesSubmit = e => {
        e.preventDefault();
        try {
            setIsLoading9(true);
            AdminService.setProviderSpecialities(dashboardData.providerSpecialities)
                .then(data => {
                    Alert.info(data.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setProviderSpecialitiesUpdated(false);
                    setIsLoading9(false);
                })
                .catch(error => {
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading9(false);
                });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading9(false);
            GeneralService.logError('at Admin Dashboard handleProviderSpecialitiesSubmit', 'Error in try catch block', err.stack);
        }
    }

    const handleProviderLanguagesSubmit = e => {
        e.preventDefault();
        try {
            setIsLoading8(true);
            AdminService.setProviderLanguages(dashboardData.providerLanguages)
                .then(data => {
                    Alert.info(data.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setProviderLanguagesUpdated(false);
                    setIsLoading8(false);
                })
                .catch(error => {
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading8(false);
                });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading8(false);
            GeneralService.logError('at Admin Dashboard handleProviderLanguagesSubmit', 'Error in try catch block', err.stack);
        }
    }

    const handleMinimumAgeForClientSubmit = e => {
        e.preventDefault();
        try {
            setIsLoading6(true);
            AdminService.setMinimumAgeForClient(dashboardData.minimumAgeForClient)
                .then(data => {
                    Alert.info(data.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setMinimumAgeForClientUpdated(false);
                    setIsLoading6(false);
                })
                .catch(error => {
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading6(false);
                });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading6(false);
            GeneralService.logError('at Admin Dashboard handleMinimumAgeForClientSubmit', 'Error in try catch block', err.stack);
        }
    }

    const handleTimeSlotSizeSubmit = e => {
        e.preventDefault();
        try {
            AdminService.setTimeSlotSize(dashboardData.timeSlotSize)
                .then(data => {
                    Alert.info(data.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setTimeSlotSizeUpdated(false);
                })
                .catch(error => {
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Dashboard handleTimeSlotSizeSubmit', 'Error in try catch block', err.stack);
        }
    }

    const handleAdvanceBookingLimitSubmit = e => {
        e.preventDefault();
        try {
            setIsLoading1(true);
            AdminService.setAdvanceBookingLimit(dashboardData.advanceBookingLimit)
                .then(data => {
                    Alert.info(data.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setAdvanceBookingLimitUpdated(false);
                    setIsLoading1(false);
                })
                .catch(error => {
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading1(false);
                });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading1(false);
            GeneralService.logError('at Admin Dashboard handleAdvanceBookingLimitSubmit', 'Error in try catch block', err.stack);
        }
    }

    const handleMinimumServiceChargesSubmit = e => {
        e.preventDefault();
        try {
            setIsLoading4(true);
            AdminService.setMinimumServiceCharges(dashboardData.minimumServiceCharges)
                .then(data => {
                    Alert.info(data.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setMinimumServiceChargesUpdated(false);
                    setIsLoading4(false);
                })
                .catch(error => {
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading4(false);
                });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading4(false);
            GeneralService.logError('at Admin Dashboard handleMinimumServiceChargesSubmit', 'Error in try catch block', err.stack);
        }
    }

    const handleMinimumReportChargeSubmit = e => {
        e.preventDefault();
        try {
            setIsLoading3(true);
            AdminService.setMinimumReportCharge(dashboardData.minimumReportCharge)
                .then(data => {
                    Alert.info(data.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setMinimumReportChargeUpdated(false);
                    setIsLoading3(false);
                })
                .catch(error => {
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading3(false);
                });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading3(false);
            GeneralService.logError('at Admin Dashboard handleMinimimReportChargeSubmit', 'Error in try catch block', err.stack);
        }
    }

    const handleClientSubscriptionPlanRangeSubmit = e => {
        e.preventDefault();
        try {
            setIsLoading10(true);
            AdminService.setSubscriptionPlanRange(dashboardData.clientSubscriptionPlanRange)
                .then(data => {
                    Alert.info(data.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setClientSubscriptionPlanRangeUpdated(false);
                    setIsLoading10(false);
                })
                .catch(error => {
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading10(false);
                });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading10(false);
            GeneralService.logError('at Admin Dashboard handleClientSubscriptionPlanRangeSubmit', 'Error in try catch block', err.stack);
        }
    }

    const handleGSTPercentageSubmit = e => {
        e.preventDefault();
        try {
            setIsLoading5(true);
            AdminService.setGSTPercentage(dashboardData.GSTPercentage)
                .then(data => {
                    Alert.info(data.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setGSTPercentageUpdated(false);
                    setIsLoading5(false);
                })
                .catch(error => {
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading5(false);
                });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading5(false);
            GeneralService.logError('at Admin Dashboard handleGSTPercentageSubmit', 'Error in try catch block', err.stack);
        }
    }

    return (
        <main>
            <Prompt
                when={timeSlotSizeUpdated || advanceBookingLimitUpdated || minimumAgeForClientUpdated || GSTPercentageUpdated || minimumReportChargeUpdated || minimumServiceChargesUpdated || userTitlesUpdated || providerSpecialitiesUpdated || providerLanguagesUpdated || clientSubscriptionPlanRangeUpdated}
                message='You have unsaved changes, are you sure you want to leave?'
            />
            <ErrorBoundary errorAt="At Admin Dashboard mainDiv">
                <div className="container-fluid">
                    <h3 className="mt-4">Dashboard</h3>
                    <div className="row">
                        <div className="my-4 col-lg-6">
                            <div className="card">
                                <div className="card-header">
                                    Advance Booking Limit
                                </div>
                                <div className="card-body">
                                    <Form onSubmit={handleAdvanceBookingLimitSubmit}>
                                        <label className="mb-4">
                                            Specify number of days in advance a session may be booked
                                        </label>
                                        <FormGroup row>
                                            <Col sm={6}>
                                                <InputGroup>
                                                    <Input type="number" min={0} name="advanceBookingLimit" id="advanceBookingLimit" value={dashboardData.advanceBookingLimit} onChange={handleChange} required />
                                                    <InputGroupAddon addonType="append">days</InputGroupAddon>
                                                </InputGroup>
                                            </Col>
                                        </FormGroup>
                                        <Button type="submit" className="btn mt-2" style={advanceBookingLimitUpdated ? theme.buttonWarning : theme.button} disabled={isLoading1}>{isLoading1 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Save</Button>
                                    </Form>
                                </div>
                            </div>
                        </div>
                        <div className="my-4 col-lg-6">
                            <div className="card">
                                <div className="card-header">
                                    Session timeslot size
                                </div>
                                <div className="card-body">
                                    <Form onSubmit={handleTimeSlotSizeSubmit}>
                                        <label className="mb-4">
                                            Specify duration of the session in minutes
                                        </label>
                                        <FormGroup row>
                                            <Col sm={6}>
                                                <InputGroup>
                                                    <Input type="select" id="timeSlotSize" name="timeSlotSize" onChange={handleChange} value={dashboardData.timeSlotSize} required>
                                                        <option value={30}>30</option>
                                                        {/* <option value={60}>60</option> */}
                                                    </Input>
                                                    <InputGroupAddon addonType="append">minutes</InputGroupAddon>
                                                </InputGroup>
                                            </Col>
                                        </FormGroup>
                                        {/* <Button type="submit" className="btn" style={theme.button}>Save</Button> */}
                                    </Form>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="my-4 col-lg-6">
                            <div className="card">
                                <div className="card-header">
                                    Minimum Report Charge
                                </div>
                                <div className="card-body">
                                    <Form onSubmit={handleMinimumReportChargeSubmit}>
                                        <label className="mb-4">
                                            Specify minimum charges for Case Report
                                        </label>
                                        <FormGroup row>
                                            <Col sm={6}>
                                                <InputGroup>
                                                    <InputGroupAddon addonType="prepend">₹</InputGroupAddon>
                                                    <Input type="number" name="minimumReportCharge" id="minimumReportCharge" value={dashboardData.minimumReportCharge} onChange={handleChange} required />
                                                </InputGroup>
                                            </Col>
                                        </FormGroup>
                                        <Button type="submit" className="btn mt-2" style={minimumReportChargeUpdated ? theme.buttonWarning : theme.button} disabled={isLoading3}>{isLoading3 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Save</Button>
                                    </Form>
                                </div>
                            </div>
                        </div>
                        <div className="my-4 col-lg-6">
                            <div className="card">
                                <div className="card-header">
                                    Minimum Service Charges
                                </div>
                                <div className="card-body">
                                    <Form onSubmit={handleMinimumServiceChargesSubmit}>
                                        <label className="mb-4">
                                            Specify minimum charges for each service type
                                        </label>
                                        <FormGroup row>
                                            <Label sm={3} for="callRate">Call</Label>
                                            <Col sm={6}>
                                                <InputGroup>
                                                    <InputGroupAddon addonType="prepend">₹</InputGroupAddon>
                                                    <Input type="number" name="call" id="call" value={dashboardData.minimumServiceCharges.call} onChange={handleMinimumServiceChargesChange} required />
                                                    {/* <InputGroupAddon addonType="append">.00</InputGroupAddon> */}
                                                </InputGroup>
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label sm={3} for="chatRate">Chat</Label>
                                            <Col sm={6}>
                                                <InputGroup>
                                                    <InputGroupAddon addonType="prepend">₹</InputGroupAddon>
                                                    <Input type="text" name="chat" id="chat" value={dashboardData.minimumServiceCharges.chat} onChange={handleMinimumServiceChargesChange} required />
                                                </InputGroup>
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label sm={3} for="videoRate">Video</Label>
                                            <Col sm={6}>
                                                <InputGroup>
                                                    <InputGroupAddon addonType="prepend">₹</InputGroupAddon>
                                                    <Input type="text" name="video" id="video" value={dashboardData.minimumServiceCharges.video} onChange={handleMinimumServiceChargesChange} required />
                                                </InputGroup>
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label sm={3} for="inPersonRate">In Person</Label>
                                            <Col sm={6}>
                                                <InputGroup>
                                                    <InputGroupAddon addonType="prepend">₹</InputGroupAddon>
                                                    <Input type="text" name="inPerson" id="inPerson" value={dashboardData.minimumServiceCharges.inPerson} onChange={handleMinimumServiceChargesChange} required />
                                                </InputGroup>
                                            </Col>
                                        </FormGroup>
                                        <Button type="submit" className="btn mt-2" style={minimumServiceChargesUpdated ? theme.buttonWarning : theme.button} disabled={isLoading4}>{isLoading4 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Save</Button>
                                    </Form>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="my-4 col-lg-6">
                            <div className="card">
                                <div className="card-header">
                                    GST percentage
                                </div>
                                <div className="card-body">
                                    <Form onSubmit={handleGSTPercentageSubmit}>
                                        <label className="mb-4">
                                            Specify Goods and Service Tax (GST) on payments
                                        </label>
                                        <FormGroup row>
                                            <Col sm={6}>
                                                <InputGroup>
                                                    <Input type="number" min={0} max={100} name="GSTPercentage" id="GSTPercentage" value={dashboardData.GSTPercentage} onChange={handleChange} required />
                                                    <InputGroupAddon addonType="append">%</InputGroupAddon>
                                                </InputGroup>
                                            </Col>
                                        </FormGroup>
                                        <Button type="submit" className="btn mt-2" style={GSTPercentageUpdated ? theme.buttonWarning : theme.button} disabled={isLoading5}>{isLoading5 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Save</Button>
                                    </Form>
                                </div>
                            </div>
                        </div>
                        <div className="my-4 col-lg-6">
                            <div className="card">
                                <div className="card-header">
                                    Minimum age for Client
                                </div>
                                <div className="card-body">
                                    <Form onSubmit={handleMinimumAgeForClientSubmit}>
                                        <label className="mb-4">
                                            Decide the minimum age for Client to get registered in platform
                                        </label>
                                        <FormGroup row>
                                            <Col sm={6}>
                                                <InputGroup>
                                                    <Input type="number" min={0} name="minimumAgeForClient" id="minimumAgeForClient" value={dashboardData.minimumAgeForClient} onChange={handleChange} required />
                                                    <InputGroupAddon addonType="append">years</InputGroupAddon>
                                                </InputGroup>
                                            </Col>
                                        </FormGroup>
                                        <Button type="submit" className="btn mt-2" style={minimumAgeForClientUpdated ? theme.buttonWarning : theme.button} disabled={isLoading6}>{isLoading6 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Save</Button>
                                    </Form>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="my-4 col-lg-6">
                            <div className="card">
                                <div className="card-header">
                                    Titles for users
                                </div>
                                <div className="card-body">
                                    <Form onSubmit={handleUserTitlesSubmit}>
                                        <label className="mb-4">
                                            Set list of titles for users (Ex: Mr., Mrs.)
                                        </label>
                                        <FormGroup>
                                            {
                                                dashboardData.userTitles.map((item, index) => (
                                                    <InputGroup key={index} className="mb-2">
                                                        <Input type="text" value={item} disabled style={theme.formInput} />
                                                        <InputGroupAddon className="ml-2" addonType="append">
                                                            <Button type="button" style={{ backgroundColor: "#9F1D1D" }} id={index} onClick={() => handleRemoveItem('title', index)}>Remove</Button>
                                                        </InputGroupAddon>
                                                    </InputGroup>
                                                ))
                                            }
                                            <InputGroup>
                                                <Input type="text" id="title" name="titlesInput" style={theme.formInput}></Input>
                                                <InputGroupAddon className="ml-2" addonType="append">
                                                    <Button style={{ backgroundColor: "#384E73" }} onClick={() => handleAddItem('title')}>Add</Button>
                                                </InputGroupAddon>
                                            </InputGroup>
                                        </FormGroup>
                                        <Button type="submit" className="btn mt-2" style={userTitlesUpdated ? theme.buttonWarning : theme.button} disabled={isLoading7}>{isLoading7 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Save</Button>
                                    </Form>
                                </div>
                            </div>
                        </div>
                        <div className="my-4 col-lg-6">
                            <div className="card">
                                <div className="card-header">
                                    Languages for counsellor
                                </div>
                                <div className="card-body">
                                    <Form onSubmit={handleProviderLanguagesSubmit}>
                                        <label className="mb-4">
                                            Set list of languages counsellor can select
                                        </label>
                                        <FormGroup>
                                            {
                                                dashboardData.providerLanguages.map((item, index) => (
                                                    <InputGroup key={index} className="mb-2">
                                                        <Input type="text" value={item} disabled style={theme.formInput} />
                                                        <InputGroupAddon className="ml-2" addonType="append">
                                                            <Button type="button" style={{ backgroundColor: "#9F1D1D" }} id={index} onClick={() => handleRemoveItem('language', index)}>Remove</Button>
                                                        </InputGroupAddon>
                                                    </InputGroup>
                                                ))
                                            }
                                            <InputGroup>
                                                <Input type="text" id="language" name="languagesInput" style={theme.formInput}></Input>
                                                <InputGroupAddon className="ml-2" addonType="append">
                                                    <Button style={{ backgroundColor: "#384E73" }} onClick={() => handleAddItem('language')}>Add</Button>
                                                </InputGroupAddon>
                                            </InputGroup>
                                        </FormGroup>
                                        <Button type="submit" className="btn mt-2" style={providerLanguagesUpdated ? theme.buttonWarning : theme.button} disabled={isLoading8}>{isLoading8 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Save</Button>
                                    </Form>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="my-4 col-lg-6">
                            <div className="card">
                                <div className="card-header">
                                    Specialities for counsellor
                                </div>
                                <div className="card-body">
                                    <Form onSubmit={handleProviderSpecialitiesSubmit}>
                                        <label className="mb-4">
                                            Set list of specialities counsellor can select
                                        </label>
                                        <FormGroup>
                                            {
                                                dashboardData.providerSpecialities.map((item, index) => (
                                                    <InputGroup key={index} className="mb-2">
                                                        <Input type="text" value={item} disabled style={theme.formInput} />
                                                        <InputGroupAddon className="ml-2" addonType="append">
                                                            <Button type="button" style={{ backgroundColor: "#9F1D1D" }} id={index} onClick={() => handleRemoveItem('speciality', index)}>Remove</Button>
                                                        </InputGroupAddon>
                                                    </InputGroup>
                                                ))
                                            }
                                            <InputGroup>
                                                <Input type="text" id="speciality" name="specialitiesInput" style={theme.formInput}></Input>
                                                <InputGroupAddon className="ml-2" addonType="append">
                                                    <Button style={{ backgroundColor: "#384E73" }} onClick={() => handleAddItem('speciality')}>Add</Button>
                                                </InputGroupAddon>
                                            </InputGroup>
                                        </FormGroup>
                                        <Button type="submit" className="btn mt-2" style={providerSpecialitiesUpdated ? theme.buttonWarning : theme.button} disabled={isLoading9}>{isLoading9 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Save</Button>
                                    </Form>
                                </div>
                            </div>
                        </div>
                        <div className="my-4 col-lg-6">
                            <div className="card">
                                <div className="card-header">
                                    Client Subscription Plan Price Range Charges
                                </div>
                                <div className="card-body">
                                    <Form onSubmit={handleClientSubscriptionPlanRangeSubmit}>
                                        <label className="mb-4">
                                            Specify Client Subscription Plan Price Range
                                        </label>
                                        <FormGroup row>
                                            <Label sm={3} for="from">From</Label>
                                            <Col sm={6}>
                                                <InputGroup>
                                                    <Input type="number" name="from" id="from" value={dashboardData.clientSubscriptionPlanRange.from} onChange={handleClientSubscriptionPlanRangeChange} required />
                                                </InputGroup>
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label sm={3} for="to">To</Label>
                                            <Col sm={6}>
                                                <InputGroup>
                                                    <Input type="number" name="to" id="to" value={dashboardData.clientSubscriptionPlanRange.to} onChange={handleClientSubscriptionPlanRangeChange} required />
                                                </InputGroup>
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label sm={3} for="interval">Interval</Label>
                                            <Col sm={6}>
                                                <InputGroup>
                                                    <Input type="number" name="interval" id="interval" value={dashboardData.clientSubscriptionPlanRange.interval} onChange={handleClientSubscriptionPlanRangeChange} required />
                                                </InputGroup>
                                            </Col>
                                        </FormGroup>
                                        <Button type="submit" className="btn mt-2" style={clientSubscriptionPlanRangeUpdated ? theme.buttonWarning : theme.button} disabled={isLoading10}>{isLoading10 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Save</Button>
                                    </Form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </main>
    )

}

export default Dashboard;